import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp } from "@/components/Motion";
import PageHero from "@/components/PageHero";
import { api, money, imgUrl } from "@/lib/api";
import { useSiteMedia } from "@/context/SiteMediaContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { useSeo } from "@/lib/useSeo";

const CARD_DEFAULT_IMG = ["/gallery/photo4.webp", "/gallery/photo2.webp", "/gallery/photo7.jpg", "/gallery/photo3.webp"];

export default function Wonder() {
  const { m } = useSiteMedia();
  const { c, media } = useSiteContent();
  useSeo(c("seo_gallery_title"), c("seo_gallery_desc"));
  const hero = media["wonder_hero"] || {};
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true").then((r) => setFeatured(r.data || [])).catch((e) => console.error("Failed to load featured products", e));
  }, []);

  return (
    <div data-testid="gallery-page">
      <PageHero
        eyebrow={c("gallery_hero_subtitle", "")}
        title={c("gallery_hero_title", "")}
        image={imgUrl(m("wonder_hero", "/gallery/photo1.jpeg"))}
        overlay={hero.overlay}
        focal={hero.focal}
      />

      {/* THE COLLECTION */}
      <section className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-36">
        <FadeUp>
          {c("gallery_collection_eyebrow") && (
            <p className="text-[11px] uppercase tracking-editorial text-matte/40 mb-3">{c("gallery_collection_eyebrow")}</p>
          )}
          <h2 className="font-serif text-4xl md:text-5xl font-light mb-14 max-w-3xl">
            {c("gallery_collection_title")}
          </h2>
        </FadeUp>
        <div className="grid md:grid-cols-2 gap-x-10 gap-y-16">
          {featured.length > 0
            ? featured.slice(0, 6).map((p, i) => (
                <FadeUp key={p.id} delay={(i % 2) * 0.08}>
                  <Link to={`/shop/${p.id}`} className="group block" data-testid={`gallery-product-${p.id}`}>
                    <div className="overflow-hidden">
                      <img
                        src={imgUrl(p.images?.[0])}
                        alt={p.title}
                        className="w-full h-[46vh] md:h-[56vh] object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                      />
                    </div>
                    <div className="pt-6 flex items-end justify-between gap-6">
                      <div>
                        <p className="text-[10px] uppercase tracking-wide-sm text-plum mb-1">{p.category}</p>
                        <h3 className="font-serif text-3xl font-light">{p.title}</h3>
                      </div>
                      <span className="font-serif text-xl whitespace-nowrap">{money(p.price)}</span>
                    </div>
                  </Link>
                </FadeUp>
              ))
            : [1, 2, 3, 4].map((i) => {
                const title = c(`gallery_card_${i}_title`);
                if (!title) return null;
                return (
                  <FadeUp key={i} delay={((i - 1) % 2) * 0.08}>
                    <Link to={c(`gallery_card_${i}_link`, "/shop/all")} className="group block" data-testid={`gallery-collection-${i}`}>
                      <div className="overflow-hidden">
                        <img
                          src={imgUrl(m(`gallery_card_${i}`, CARD_DEFAULT_IMG[i - 1]))}
                          alt={title}
                          className="w-full h-[46vh] md:h-[56vh] object-cover group-hover:scale-[1.03] transition-transform duration-[1200ms] ease-out"
                        />
                      </div>
                      <div className="pt-6 flex items-end justify-between gap-6">
                        <div>
                          <h3 className="font-serif text-3xl font-light">{title}</h3>
                          <p className="text-matte/60 leading-relaxed mt-2 max-w-md">{c(`gallery_card_${i}_desc`)}</p>
                        </div>
                        <ArrowRight className="text-plum group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0" />
                      </div>
                    </Link>
                  </FadeUp>
                );
              })}
        </div>
      </section>

      {/* EXHIBITIONS */}
      <section className="bg-matte text-ivory">
        <div className="max-w-[1500px] mx-auto px-6 md:px-10 py-24 md:py-36">
          <FadeUp>
            {c("gallery_exhibitions_eyebrow") && (
              <p className="text-[11px] uppercase tracking-editorial text-brass mb-4">{c("gallery_exhibitions_eyebrow")}</p>
            )}
            <h2 className="font-serif text-4xl md:text-5xl font-light max-w-3xl">
              {c("gallery_exhibitions_title")}
            </h2>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-14 mt-16">
            {[1, 2, 3].map((i) => {
              const title = c(`gallery_exh_${i}_title`);
              if (!title) return null;
              return (
                <FadeUp key={i} delay={(i - 1) * 0.08}>
                  <div className="border-t border-ivory/15 pt-6">
                    <h3 className="font-serif text-2xl md:text-3xl font-light mb-4">{title}</h3>
                    <p className="text-ivory/60 leading-relaxed">{c(`gallery_exh_${i}_desc`)}</p>
                  </div>
                </FadeUp>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISIT CTA */}
      {(c("gallery_visit_text") || c("gallery_visit_cta_label") || c("gallery_shop_cta_label")) && (
        <section className="max-w-[1100px] mx-auto px-6 md:px-10 py-24 md:py-36 text-center">
          <FadeUp>
            {c("gallery_visit_text") && (
              <p className="font-serif text-3xl md:text-4xl font-light leading-[1.3] max-w-2xl mx-auto">
                {c("gallery_visit_text")}
              </p>
            )}
            <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
              {c("gallery_visit_cta_label") && (
                <Link
                  to={c("gallery_visit_cta_link", "/visit")}
                  data-testid="gallery-visit-btn"
                  className="group inline-flex items-center gap-3 bg-matte text-ivory px-9 py-4 text-[12px] uppercase tracking-editorial hover:bg-plum transition-colors duration-300"
                >
                  {c("gallery_visit_cta_label", "Schedule a Visit")}
                  <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
              )}
              {c("gallery_shop_cta_label") && (
                <Link
                  to={c("gallery_shop_cta_link", "/shop/all")}
                  data-testid="gallery-shop-btn"
                  className="inline-block border border-matte px-9 py-4 text-[12px] uppercase tracking-editorial hover:bg-matte hover:text-ivory transition-colors duration-300"
                >
                  {c("gallery_shop_cta_label", "Enter the Shop")}
                </Link>
              )}
            </div>
          </FadeUp>
        </section>
      )}
    </div>
  );
}
